import React, { useState } from "react";

import { formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import { blockedTaxLabel } from "../utils/fiatFields";

function finiteOrNull(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
}

function money(value, currencySymbol, digits = 6) {
  const number = finiteOrNull(value);
  return number === null ? "—" : `${currencySymbol} ${number.toFixed(digits)}`;
}

function compactNumber(value, digits = 6) {
  const number = finiteOrNull(value);

  if (number === null) {
    return "—";
  }

  return number
    .toFixed(digits)
    .replace(/\.?0+$/, "");
}

function lower(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function upper(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function nftContractOf(row = {}) {
  return lower(
    row?.nftContract ||
      row?.nft_contract ||
      row?.contract ||
      row?.contractAddress
  );
}

function nftTokenIdOf(row = {}) {
  return (
    row?.tokenId ??
    row?.nft_token_id ??
    row?.token_id ??
    row?.relatedMintTokenId ??
    null
  );
}

function reviewText(row, language) {
  const label = blockedTaxLabel(row, language);
  const reason = String(row?.reviewReason || "");
  return reason ? `${label} (${reason})` : label;
}

function taxStatus(leg, language) {
  if (
    leg?.reviewRequired === true ||
    leg?.taxComputationSkipped === true
  ) {
    return reviewText(leg, language);
  }

  if (leg?.taxable === true) {
    return language === "en" ? "Taxable" : "Steuerpflichtig";
  }

  if (leg?.taxable === false) {
    return language === "en" ? "Tax-free" : "Steuerfrei";
  }

  return "—";
}

export default function NftTransformDetailRow({
  tx,
  colSpan = 10,
  currencySymbol = "€",
  currencyCode = "EUR",
  language = "de",
  pdfExportMode = false,
  onSaveResultFmv = null,
}) {
  const isNoRealization = tx?.noRealization === true;

  const isItalyNeutral = String(tx?.taxTreatment || "").includes(
    "italy_neutral"
  );

  /*
   * Aggregate DE disposal is NOT itself one of the 3 source NFTs.
   */
  const disposalLegs = Array.isArray(tx?.nftTransformDisposals)
    ? tx.nftTransformDisposals.filter(
        (row) => row?.nftTransformAggregateDisposal !== true
      )
    : [];

  const provenanceLegs = Array.isArray(tx?.sourceAcquisitionLegs)
    ? tx.sourceAcquisitionLegs
    : [];

  const fallbackSourceAcquisitions = Array.isArray(tx?.sourceAcquisitions)
    ? tx.sourceAcquisitions.map((entry, index) => ({
        index,
        nftContract:
          entry?.source?.nftContract || entry?.source?.contract || null,
        tokenId: entry?.source?.tokenId ?? entry?.source?.nft_token_id ?? null,
        nftName:
          entry?.source?.nftName ||
          entry?.source?.nft_name ||
          tx?.nftName ||
          "NFT",
        acquisitionTimestamp: entry?.acquisitionTs || null,
        purchaseTimestamp: entry?.acquisitionTs || null,
        nftAcquisitionCostFiat: entry?.costBasisFiat ?? null,
        sourceFmvFiat: entry?.fmvFiat ?? null,
        holdingDays: entry?.holdingDays ?? null,
        taxable: entry?.taxable ?? null,
        acquisitionStatus: entry?.acquisitionStatus || null,
        costBasisSource: entry?.costBasisSource || null,
      }))
    : [];

  const sourceLegs =
    disposalLegs.length > 0
      ? disposalLegs
      : provenanceLegs.length > 0
        ? provenanceLegs
        : fallbackSourceAcquisitions;

  const jurisdiction = upper(
    tx?.taxJurisdiction ||
      tx?.jurisdiction ||
      tx?.selectedCountry ||
      tx?.country
  );

  const isUS =
    jurisdiction === "US" ||
    jurisdiction === "USA" ||
    jurisdiction.includes("UNITED STATES");

  const sourceAcquisitionEntries = Array.isArray(tx?.sourceAcquisitions)
    ? tx.sourceAcquisitions
    : [];

  /*
   * Match the tax disposal leg back to the actual acquisition
   * used by the backend to resolve its historical basis.
   *
   * Primary identity = contract + tokenId.
   * Index is only a final presentation fallback.
   */
  const sourceAcquisitionForLeg = (leg, index) => {
    const legContract = nftContractOf(leg);
    const legTokenId = nftTokenIdOf(leg);

    const exact = sourceAcquisitionEntries.find((entry) => {
      const source = entry?.source || {};
      const sourceContract = nftContractOf(source);
      const sourceTokenId = nftTokenIdOf(source);

      if (legTokenId == null || sourceTokenId == null) {
        return false;
      }

      if (String(legTokenId) !== String(sourceTokenId)) {
        return false;
      }

      /*
       * Token-ID alone is accepted only when one side
       * genuinely has no contract identity.
       */
      return (
        !legContract ||
        !sourceContract ||
        legContract === sourceContract
      );
    });

    return exact || sourceAcquisitionEntries[index] || null;
  };

  /*
   * Human-readable audit explanation of WHY this NFT
   * has exactly this historical cost basis.
   *
   * IMPORTANT:
   * Nothing is recalculated here.
   * The frontend only explains the acquisition row
   * already selected by the backend.
   */
  const acquisitionProvenanceForLeg = (leg, index) => {
    const entry = sourceAcquisitionForLeg(leg, index);

    const acquisitionRow =
      entry?.acquisitionRow || entry?.rawAcquisitionEvent || {};

    const acquisitionClass = lower(
      acquisitionRow?.classification || acquisitionRow?.type
    );

    const acquisitionStatus = lower(
      entry?.acquisitionStatus ||
        acquisitionRow?.acquisitionStatus ||
        leg?.acquisitionStatus
    );

    const costBasisSource = lower(
      entry?.costBasisSource ||
        acquisitionRow?.costBasisSource ||
        leg?.costBasisSource
    );

    const basis = finiteOrNull(
      leg?.grossBuyFiat,
      leg?.nftAcquisitionCostFiat,
      leg?.costBasisFiat,
      entry?.costBasisFiat
    );

    /*
     * Result of an earlier ReMint/Melt.
     * Never label this as a new 100-VTHO mint.
     */
    const isPriorTransform =
      acquisitionClass === "nft_transform_acquisition" ||
      acquisitionStatus.includes("nft_transform") ||
      costBasisSource.includes("nft_transform");

    if (isPriorTransform) {
      const carryover =
        acquisitionRow?.noRealization === true &&
        acquisitionRow?.basisCarryover === true;

      return {
        main:
          language === "en"
            ? carryover
              ? "Previous NFT conversion"
              : "Previous ReMint / Melt"
            : carryover
              ? "Vorheriger NFT-Transform"
              : "Vorheriger ReMint / Melt",
        sub:
          language === "en"
            ? carryover
              ? "Historical basis carried over"
              : "Basis = result FMV of that exchange"
            : carryover
              ? "Historische Basis übernommen"
              : "Basis = damaliger Result-FMV",
      };
    }

    const paymentToken = upper(
      acquisitionRow?.nftPaymentToken ||
        acquisitionRow?.acquisitionToken ||
        acquisitionRow?.paymentToken ||
        acquisitionRow?.token
    );

    const paymentAmount = finiteOrNull(
      acquisitionRow?.nftPaymentAmount,
      acquisitionRow?.nftUnitPaymentVtho,
      acquisitionRow?.acquisitionCostToken,
      acquisitionRow?.paymentAmount,
      acquisitionRow?.amount,
      acquisitionRow?.quantity,
      acquisitionRow?.qty
    );

    const paymentUnitPrice = finiteOrNull(
      acquisitionRow?.unitPriceFiat,
      acquisitionRow?.priceFiat,
      acquisitionRow?.euroPrice,
      acquisitionRow?.priceEUR
    );

    if (paymentToken && paymentAmount !== null && paymentAmount > 0) {
      const main =
        paymentUnitPrice !== null
          ? `${compactNumber(paymentAmount, 8)} ${paymentToken} × ${money(
              paymentUnitPrice,
              currencySymbol,
              8
            )}`
          : `${compactNumber(paymentAmount, 8)} ${paymentToken}`;

      let sub =
        language === "en"
          ? "Payment valued at acquisition"
          : "Zahlung zum Anschaffungszeitpunkt bewertet";

      if (
        costBasisSource === "vemugs_batch_payment_fmv_allocation" ||
        acquisitionRow?.nftBatchTotalPaymentVtho != null
      ) {
        sub =
          language === "en"
            ? "VeMugs brew payment allocated per NFT"
            : "VeMugs-Brew-Zahlung je NFT (60 + 40 VTHO zusammengeführt)";
      }

      return {
        main,
        sub,
      };
    }

    if (costBasisSource === "verified_free_mint") {
      return {
        main:
          language === "en"
            ? "Verified free mint"
            : "Verifizierter Free Mint",
        sub:
          basis === null
            ? ""
            : language === "en"
              ? "Confirmed acquisition basis"
              : "Bestätigte Anschaffungsbasis",
      };
    }

    if (costBasisSource === "user_confirmed_zero_cost_basis") {
      return {
        main:
          language === "en"
            ? "Manual basis confirmation"
            : "Manuell bestätigte AK",
        sub: "",
      };
    }

    if (costBasisSource.includes("receipt")) {
      return {
        main:
          language === "en"
            ? "Verified on-chain payment"
            : "Verifizierte On-chain-Zahlung",
        sub:
          language === "en"
            ? "Historical payment value"
            : "Historischer Zahlungswert",
      };
    }

    return {
      main:
        language === "en"
          ? "Historical acquisition basis"
          : "Historische Anschaffungsbasis",
      sub: costBasisSource ? costBasisSource : "",
    };
  };

  const usTermForLeg = (leg) => {
    if (!isUS) {
      return null;
    }

    if (leg?.isLongTerm === true) {
      return "LT";
    }

    if (leg?.isLongTerm === false) {
      return "ST";
    }

    const short = finiteOrNull(
      leg?.shortTermProfitFiat,
      leg?.shortTermProfitEUR
    );

    const long = finiteOrNull(
      leg?.longTermProfitFiat,
      leg?.longTermProfitEUR
    );

    if (long !== null && Math.abs(long) > 1e-12) {
      return "LT";
    }

    if (short !== null) {
      return "ST";
    }

    return "—";
  };

  const result = tx?.nftTransformAcquisition || {};
  const resultTokenId =
    tx?.nftTransformResultTokenId ??
    result.tokenId ??
    tx?.tokenId ??
    "—";
  const resultName =
    result.nftName ||
    tx?.nftTransformResultNft?.nftName ||
    tx?.nftName ||
    tx?.protocol ||
    "NFT";
  const totalExchangeValue = finiteOrNull(
    tx?.disposalProceedsTotalFiat,
    tx?.totalExchangeValueFiat,
    tx?.grossSellFiat,
    tx?.resultFmvFiat,
    tx?.nftTransformResultFmvFiat,
    tx?.sourceBundleFmvFiat,
    tx?.nftTransformSourceBundleFmvFiat,
    tx?.sourceFmvTotalFiat
  );

  const resultCost = finiteOrNull(
    tx?.resultAcquisitionCostFiat,
    result.nftAcquisitionCostFiat,
    result.acquisitionCostFiat
  );

  const sourceBundleValue = finiteOrNull(
    tx?.sourceBundleFmvFiat,
    tx?.nftTransformSourceBundleFmvFiat,
    tx?.sourceFmvTotalFiat
  );

  const receivedFmvValue = finiteOrNull(
    tx?.resultFmvFiat,
    tx?.nftTransformResultFmvFiat
  );

  const resultTimestamp =
    result.purchaseTimestamp ||
    result.mintTimestamp ||
    tx?.nftTransformMintTs ||
    tx?.timestamp;

  const parentReview =
    tx?.reviewRequired === true ||
    tx?.basisReviewRequired === true ||
    tx?.taxTreatment === "review_required";

  const totalBasisFromParent = finiteOrNull(tx?.grossBuyFiat);

  const sourceBasisValues = sourceLegs.map((leg) =>
    finiteOrNull(
      leg?.grossBuyFiat,
      leg?.nftAcquisitionCostFiat,
      leg?.costBasisFiat
    )
  );

  const allSourceBasisKnown =
    sourceBasisValues.length > 0 &&
    sourceBasisValues.every((value) => value !== null);

  const totalBasis =
    totalBasisFromParent ??
    (allSourceBasisKnown
      ? sourceBasisValues.reduce((sum, value) => sum + value, 0)
      : null);

  const grossProfit = finiteOrNull(
    tx?.grossProfitFiat,
    totalExchangeValue !== null && totalBasis !== null
      ? totalExchangeValue - totalBasis
      : null
  );

  const usTermTotals = disposalLegs.reduce(
    (acc, leg) => {
      const short = finiteOrNull(
        leg?.shortTermProfitFiat,
        leg?.shortTermProfitEUR
      );

      const long = finiteOrNull(
        leg?.longTermProfitFiat,
        leg?.longTermProfitEUR
      );

      if (short !== null || long !== null) {
        acc.short += short || 0;
        acc.long += long || 0;
        acc.known = true;
        return acc;
      }

      const profit = finiteOrNull(
        leg?.grossProfitFiat,
        leg?.grossProfitEUR,
        leg?.legProfitFiat,
        leg?.legProfitEUR
      );

      if (
        profit !== null &&
        leg?.isLongTerm !== undefined &&
        leg?.isLongTerm !== null
      ) {
        if (leg.isLongTerm === true) {
          acc.long += profit;
        } else {
          acc.short += profit;
        }

        acc.known = true;
      }

      return acc;
    },
    {
      short: 0,
      long: 0,
      known: false,
    }
  );

  const shortTermProfit = usTermTotals.known
    ? usTermTotals.short
    : finiteOrNull(tx?.shortTermProfitFiat, tx?.shortTermProfitEUR);

  const longTermProfit = usTermTotals.known
    ? usTermTotals.long
    : finiteOrNull(tx?.longTermProfitFiat, tx?.longTermProfitEUR);

  const exchangeValueSide = lower(
    tx?.exchangeValueSide || result?.exchangeValueSide
  );

  const valuationBasisLabel =
    exchangeValueSide === "transferred"
      ? language === "en"
        ? "Transferred NFT bundle"
        : "Hingegebenes NFT-Bundle"
      : language === "en"
        ? "Received result NFT"
        : "Erhaltenes Ergebnis-NFT";

  const valuationBasisValue =
    exchangeValueSide === "transferred"
      ? sourceBundleValue
      : receivedFmvValue;

  const resultFmvSource = lower(
    tx?.nftTransformResultFmvSource || tx?.resultFmvSource
  );

  const valuationMethod = lower(tx?.valuationMethod);

  const primaryMintPaymentAsset = upper(
    tx?.nftTransformPrimaryMintPaymentAsset ||
      tx?.primaryMintPaymentAsset
  );

  const primaryMintPaymentAmount = finiteOrNull(
    tx?.nftTransformPrimaryMintUnitPaymentAmount,
    tx?.primaryMintUnitPaymentAmount,
    tx?.primaryMintUnitPayment
  );

  const primaryMintReferenceType = lower(
    tx?.nftTransformPrimaryMintReferenceType ||
      tx?.primaryMintReferenceType
  );

  const usesObservedPrimaryMint =
    resultFmvSource === "contemporaneous_primary_random_mint" ||
    valuationMethod.includes("observed_primary_random_mint") ||
    primaryMintReferenceType === "observed_primary_mint";

  const usesProtocolPrimaryMintProxy =
    resultFmvSource === "protocol_primary_mint_price_proxy" ||
    valuationMethod.includes("protocol_primary_mint_price_proxy") ||
    primaryMintReferenceType === "protocol_price_rule";

  const primaryMintSuffix =
    primaryMintPaymentAmount !== null && primaryMintPaymentAsset
      ? ` · ${compactNumber(primaryMintPaymentAmount, 8)} ${primaryMintPaymentAsset}`
      : "";

  let valuationSourceLabel;

  if (usesObservedPrimaryMint) {
    valuationSourceLabel =
      language === "en"
        ? `Observed primary-mint comparable${primaryMintSuffix}`
        : `Beobachteter Primary-Mint-Vergleich${primaryMintSuffix}`;
  } else if (usesProtocolPrimaryMintProxy) {
    valuationSourceLabel =
      language === "en"
        ? `Protocol primary-mint price proxy${primaryMintSuffix}`
        : `Protokoll-Mintpreis-Proxy${primaryMintSuffix}`;
  } else if (resultFmvSource === "manual_verified") {
    valuationSourceLabel =
      language === "en"
        ? "Manually verified FMV"
        : "Manuell bestätigter FMV";
  } else if (resultFmvSource === "marketplace_comparable") {
    valuationSourceLabel =
      language === "en"
        ? "Marketplace comparable"
        : "Marketplace-Vergleich";
  } else if (resultFmvSource === "collection_market_data") {
    valuationSourceLabel =
      language === "en"
        ? "Collection market data"
        : "Collection-Marktdaten";
  } else if (exchangeValueSide === "transferred") {
    valuationSourceLabel =
      language === "en"
        ? "Transferred-bundle FMV"
        : "FMV des hingegebenen Bundles";
  } else {
    valuationSourceLabel =
      language === "en"
        ? "Result-NFT FMV"
        : "FMV des Ergebnis-NFTs";
  }

  const allocationMethod = lower(tx?.proceedsAllocationMethod);

  const allocationExplanation =
    allocationMethod === "protocol_equal_input_units"
      ? language === "en"
        ? "Proceeds allocation: the total exchange value is allocated across the three equally required protocol input units (1/3 each). This allocation does not assert equal standalone FMVs of the three NFTs."
        : "Erlösaufteilung: Der Gesamtwert des Tauschs wird auf die drei gleich erforderlichen Protokoll-Inputs verteilt (je 1/3). Diese Aufteilung bedeutet nicht, dass die drei NFTs denselben eigenständigen Marktwert hatten."
      : allocationMethod === "source_fmv_pro_rata"
        ? language === "en"
          ? "Proceeds allocation: allocated pro rata using the individually determined source-NFT FMVs."
          : "Erlösaufteilung: anteilig nach den individuell ermittelten FMVs der eingesetzten NFTs."
        : null;

  const taxableProfit = finiteOrNull(tx?.taxableProfitFiat);
  const nonTaxableProfit = finiteOrNull(tx?.nonTaxableProfitFiat);

  /*
   * Only real individual disposal legs with allocated proceeds
   * imply a real allocation. Review-rows and aggregate bundles
   * must not look as if proceeds were already split.
   */
  const hasIndividualAllocation = disposalLegs.some(
    (row) =>
      row?.taxComputationSkipped !== true &&
      row?.reviewRequired !== true &&
      Number.isFinite(Number(row?.grossSellFiat))
  );

  const reviewReason = String(
    tx?.reviewReason ||
      tx?.taxReviewReason ||
      ""
  ).toUpperCase();

  /*
   * FMV-Eingabe NUR bei echten Bewertungsproblemen.
   * Basis-/Anschaffungsprobleme dürfen hier keine
   * Marktwert-Eingabe auslösen.
   */
  const isFmvReview = new Set([
    "NFT_TRANSFORM_RESULT_FMV_MISSING",
    "NFT_TRANSFORM_BOTH_SIDES_FMV_MISSING",
    "NFT_TRANSFORM_SOURCE_FMV_MISSING",
    "NFT_TRANSFORM_OVERRIDE_CURRENCY_MISMATCH",
  ]).has(reviewReason);

  const canSaveTransformFmv =
    !pdfExportMode &&
    typeof onSaveResultFmv === "function" &&
    Boolean(tx?.nftTransformLifecycleKey) &&
    isFmvReview &&
    !isNoRealization;

  /*
   * Primärer Bewertungsweg:
   * FMV des erhaltenen NFTs.
   */
  const canEnterResultFmv =
    canSaveTransformFmv &&
    (receivedFmvValue === null ||
      reviewReason === "NFT_TRANSFORM_OVERRIDE_CURRENCY_MISMATCH");

  /*
   * Alternativer Bewertungsweg:
   * Gesamt-FMV der hingegebenen NFTs.
   *
   * Keine individuelle Aufteilung erforderlich,
   * solange die TaxPolicy einen Bundle-Disposal
   * verwenden kann.
   */
  const canEnterSourceBundleFmv =
    canSaveTransformFmv &&
    (sourceBundleValue === null ||
      reviewReason === "NFT_TRANSFORM_OVERRIDE_CURRENCY_MISMATCH");

  const needsTermAllocationFmvs =
    !isNoRealization &&
    reviewReason ===
      "NFT_TRANSFORM_TERM_ALLOCATION_REQUIRED" &&
    sourceLegs.length > 0;

  const canEnterIndividualSourceFmvs =
    !pdfExportMode &&
    typeof onSaveResultFmv === "function" &&
    Boolean(tx?.nftTransformLifecycleKey) &&
    needsTermAllocationFmvs;

  const termAllocationSaveBlockedReason =
    needsTermAllocationFmvs && !canEnterIndividualSourceFmvs
      ? !tx?.nftTransformLifecycleKey
        ? language === "en"
          ? "Lifecycle key missing — cannot save source FMVs for this transform."
          : "Lifecycle-Key fehlt — Source-FMVs können für diesen Transform nicht gespeichert werden."
        : typeof onSaveResultFmv !== "function"
          ? language === "en"
            ? "Save handler unavailable — reload the analysis page and try again."
            : "Speicherfunktion nicht verfügbar — Analyse-Seite neu laden und erneut versuchen."
          : null
      : null;

  const [resultFmvInput, setResultFmvInput] = useState("");
  const [sourceBundleFmvInput, setSourceBundleFmvInput] = useState("");
  const [sourceFmvInputs, setSourceFmvInputs] = useState({});
  const [savingFmv, setSavingFmv] = useState(false);
  const [saveError, setSaveError] = useState("");

  const parsePositiveFmv = (raw) => {
    const value = Number(String(raw).replace(",", "."));
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const sourceFmvValueForLeg = (leg, index) =>
    finiteOrNull(
      leg?.sourceFmvFiat,
      leg?.fmvFiat,
      leg?.marketValueFiat,
      sourceLegs[index]?.sourceFmvFiat
    );

  const transformIdentityPayload = {
    lifecycleKey: tx.nftTransformLifecycleKey,
    fiatCurrency: currencyCode,
    resultTokenId: tx.nftTransformResultTokenId ?? result.tokenId ?? null,
    resultContract:
      tx.nftContract || tx.nftTransformResultNft?.nftContract || null,
  };

  const saveTransformFmv = async (payload) => {
    setSavingFmv(true);
    setSaveError("");

    try {
      await onSaveResultFmv({
        ...transformIdentityPayload,
        ...payload,
      });
    } catch (error) {
      setSaveError(
        error?.message ||
          (language === "en"
            ? "Could not save the market value."
            : "Marktwert konnte nicht gespeichert werden.")
      );
    } finally {
      setSavingFmv(false);
    }
  };

  const submitResultFmv = async (event) => {
    event.preventDefault();

    const value = parsePositiveFmv(resultFmvInput);
    if (value === null) {
      setSaveError(
        language === "en"
          ? "Enter a positive market value."
          : "Bitte einen positiven Marktwert eingeben."
      );
      return;
    }

    await saveTransformFmv({
      resultFmvFiat: value,
    });
  };

  const submitSourceBundleFmv = async (event) => {
    event.preventDefault();

    const value = parsePositiveFmv(sourceBundleFmvInput);
    if (value === null) {
      setSaveError(
        language === "en"
          ? "Enter a positive market value."
          : "Bitte einen positiven Marktwert eingeben."
      );
      return;
    }

    await saveTransformFmv({
      sourceBundleFmvFiat: value,
    });
  };

  const submitIndividualSourceFmvs =
    async (event) => {
      event.preventDefault();

      const sourceFmvFiat = {};

      for (
        const [index, leg]
        of sourceLegs.entries()
      ) {
        const tokenId =
          leg?.tokenId == null
            ? ""
            : String(leg.tokenId);

        const inputKey =
          tokenId || `source-${index}`;

        const current =
          sourceFmvValueForLeg(
            leg,
            index
          );

        const raw =
          sourceFmvInputs[inputKey] ??
          (current ?? "");

        const value =
          parsePositiveFmv(raw);

        if (
          !tokenId ||
          value === null
        ) {
          setSaveError(
            language === "en"
              ? "Enter a positive FMV for every source NFT."
              : "Bitte für jedes eingesetzte NFT einen positiven FMV eingeben."
          );

          return;
        }

        sourceFmvFiat[tokenId] =
          value;
      }

      /*
       * Bereits bekannte Gesamtwerte ebenfalls
       * mitschicken, damit der Override vollständig
       * self-contained ist.
       */
      await saveTransformFmv({
        ...(receivedFmvValue !== null
          ? {
              resultFmvFiat:
                receivedFmvValue,
            }
          : {}),

        ...(sourceBundleValue !== null
          ? {
              sourceBundleFmvFiat:
                sourceBundleValue,
            }
          : {}),

        sourceFmvFiat,
      });
    };

  return (
    <tr
      className={
        pdfExportMode
          ? "bg-slate-50/80 print:break-inside-avoid"
          : "bg-slate-50/80"
      }
      data-testid="nft-transform-details"
      data-pdf-expanded={
        pdfExportMode
          ? "true"
          : undefined
      }
      style={
        pdfExportMode
          ? {
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }
          : undefined
      }
    >
      <td colSpan={colSpan} className="px-6 py-4 text-sm text-slate-700">
        <div className="space-y-4">
          {parentReview ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 font-medium text-amber-900">
              {reviewText(tx, language)}
              {canEnterResultFmv ? (
                <form
                  className="mt-3 flex flex-wrap items-end gap-2 font-normal text-slate-800"
                  onSubmit={submitResultFmv}
                >
                  <label className="flex min-w-[14rem] flex-col gap-1 text-xs">
                    <span>
                      {language === "en"
                        ? `Result NFT FMV (${currencyCode})`
                        : `FMV erhaltenes NFT (${currencyCode})`}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={resultFmvInput}
                      onChange={(event) => setResultFmvInput(event.target.value)}
                      className="rounded border border-amber-300 bg-white px-2 py-1 text-sm"
                      placeholder={currencyCode === "USD" ? "0.24" : "0,24"}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={savingFmv}
                    className="rounded bg-amber-800 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                  >
                    {savingFmv
                      ? language === "en"
                        ? "Saving…"
                        : "Speichern…"
                      : language === "en"
                        ? "Save and recalculate"
                        : "Speichern und neu berechnen"}
                  </button>
                </form>
              ) : null}

              {canEnterSourceBundleFmv ? (
                <form
                  className="mt-3 flex flex-wrap items-end gap-2 font-normal text-slate-800"
                  onSubmit={submitSourceBundleFmv}
                >
                  <label className="flex min-w-[14rem] flex-col gap-1 text-xs">
                    <span>
                      {language === "en"
                        ? `Source NFTs total FMV (${currencyCode})`
                        : `FMV eingesetzte NFTs gesamt (${currencyCode})`}
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={sourceBundleFmvInput}
                      onChange={(event) =>
                        setSourceBundleFmvInput(event.target.value)
                      }
                      className="rounded border border-amber-300 bg-white px-2 py-1 text-sm"
                      placeholder={currencyCode === "USD" ? "0.24" : "0,24"}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={savingFmv}
                    className="rounded bg-amber-800 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                  >
                    {savingFmv
                      ? language === "en"
                        ? "Saving…"
                        : "Speichern…"
                      : language === "en"
                        ? "Save source FMV and recalculate"
                        : "Quellen-FMV speichern und neu berechnen"}
                  </button>
                </form>
              ) : null}

              {canEnterIndividualSourceFmvs ? (
                <form
                  className="mt-3 space-y-3 font-normal text-slate-800"
                  onSubmit={submitIndividualSourceFmvs}
                >
                  <div className="text-xs font-medium text-amber-950">
                    {language === "en"
                      ? `Enter an individual FMV (${currencyCode}) for every source NFT so Short-Term and Long-Term proceeds can be allocated.`
                      : `Bitte für jedes eingesetzte NFT einen individuellen FMV (${currencyCode}) eingeben, damit Short- und Long-Term-Erlöse aufgeteilt werden können.`}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {sourceLegs.map((leg, index) => {
                      const tokenId =
                        leg?.tokenId == null
                          ? ""
                          : String(leg.tokenId);
                      const inputKey =
                        tokenId || `source-${index}`;
                      const current =
                        sourceFmvValueForLeg(leg, index);
                      return (
                        <label
                          key={inputKey}
                          className="flex min-w-[12rem] flex-col gap-1 text-xs"
                        >
                          <span>
                            {leg.nftName || tx?.nftName || "NFT"}
                            {tokenId ? ` #${tokenId}` : ""}
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={
                              sourceFmvInputs[inputKey] ??
                              (current == null ? "" : String(current))
                            }
                            onChange={(event) =>
                              setSourceFmvInputs((prev) => ({
                                ...prev,
                                [inputKey]: event.target.value,
                              }))
                            }
                            className="rounded border border-amber-300 bg-white px-2 py-1 text-sm"
                            placeholder={
                              currencyCode === "USD" ? "0.08" : "0,08"
                            }
                          />
                        </label>
                      );
                    })}
                  </div>
                  <button
                    type="submit"
                    disabled={savingFmv}
                    className="rounded bg-amber-800 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                  >
                    {savingFmv
                      ? language === "en"
                        ? "Saving…"
                        : "Speichern…"
                      : language === "en"
                        ? "Save source FMVs and recalculate"
                        : "Quellen-FMVs speichern und neu berechnen"}
                  </button>
                </form>
              ) : termAllocationSaveBlockedReason ? (
                <div className="mt-3 text-xs font-normal text-amber-950">
                  {termAllocationSaveBlockedReason}
                </div>
              ) : needsTermAllocationFmvs && pdfExportMode ? (
                <div className="mt-3 text-xs font-normal text-amber-950">
                  {language === "en"
                    ? "Individual source FMVs are required before this mixed short-/long-term transform can be finalized."
                    : "Für diesen Transform mit gemischtem Short-/Long-Term fehlen noch individuelle Source-FMVs."}
                </div>
              ) : null}

              {saveError ? (
                <div className="mt-2 text-xs font-normal text-red-700">
                  {saveError}
                </div>
              ) : null}
            </div>
          ) : null}

          <div>
            <div className="mb-2 font-medium text-slate-800">
              {language === "en" ? "Source NFTs" : "Eingesetzte NFTs"}
              {isItalyNeutral
                ? language === "en"
                  ? " (neutral carryover)"
                  : " (steuerneutraler Carryover)"
                : ""}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="pr-3 py-1">
                      {language === "en" ? "Source NFT" : "Quell-NFT"}
                    </th>
                    <th className="pr-3 py-1">Token-ID</th>
                    <th className="pr-3 py-1">
                      {language === "en" ? "Acquired" : "Anschaffung"}
                    </th>
                    <th className="pr-3 py-1 min-w-[14rem]">
                      {language === "en" ? "Basis source" : "AK-Herkunft"}
                    </th>
                    <th className="pr-3 py-1">
                      {language === "en" ? "Cost basis" : "AK"}
                    </th>
                    {hasIndividualAllocation ? (
                      <>
                        <th className="pr-3 py-1">
                          {language === "en"
                            ? "Allocated proceeds"
                            : "Zugeordneter Erlös"}
                        </th>
                        <th className="pr-3 py-1">
                          {language === "en"
                            ? "Profit / loss"
                            : "Gewinn / Verlust"}
                        </th>
                      </>
                    ) : null}
                    <th className="pr-3 py-1">
                      {language === "en" ? "Holding period" : "Haltedauer"}
                    </th>
                    <th className="py-1">
                      {isUS
                        ? "Term"
                        : language === "en"
                          ? "Tax status"
                          : "Steuerstatus"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sourceLegs.map((leg, index) => {
                    const acquisitionTimestamp =
                      leg.purchaseTimestamp ||
                      leg.mintTimestamp ||
                      leg.acquisitionTimestamp ||
                      leg.acquisitionTs;

                    const acquisitionProvenance =
                      acquisitionProvenanceForLeg(leg, index);

                    const usTerm = usTermForLeg(leg);

                    return (
                      <tr
                        key={`${leg.tokenId ?? index}-${index}`}
                        className="border-t border-slate-200"
                      >
                        <td className="pr-3 py-1.5">
                          {leg.nftName || leg.collection || tx?.nftName || "NFT"}
                        </td>
                        <td className="pr-3 py-1.5">
                          {leg.tokenId == null ? "—" : `#${leg.tokenId}`}
                        </td>
                        <td className="pr-3 py-1.5 whitespace-nowrap">
                          {acquisitionTimestamp
                            ? formatDateTimeUtcFromTimestamp(
                                acquisitionTimestamp
                              )
                            : "—"}
                        </td>
                        <td className="pr-3 py-1.5 align-top">
                          <div className="font-medium text-slate-700 whitespace-nowrap">
                            {acquisitionProvenance.main}
                          </div>
                          {acquisitionProvenance.sub ? (
                            <div className="mt-0.5 text-[10px] leading-tight text-slate-400">
                              {acquisitionProvenance.sub}
                            </div>
                          ) : null}
                        </td>
                        <td className="pr-3 py-1.5">
                          {money(
                            finiteOrNull(
                              leg.nftAcquisitionCostFiat,
                              leg.costBasisFiat,
                              leg.grossBuyFiat
                            ),
                            currencySymbol
                          )}
                        </td>
                        {hasIndividualAllocation ? (
                          <>
                            <td className="pr-3 py-1">
                              {money(
                                finiteOrNull(
                                  leg?.grossSellFiat,
                                  leg?.sourceFmvFiat
                                ),
                                currencySymbol
                              )}
                            </td>
                            <td className="pr-3 py-1">
                              {money(
                                finiteOrNull(
                                  leg?.grossProfitFiat,
                                  leg?.legProfitFiat
                                ),
                                currencySymbol
                              )}
                            </td>
                          </>
                        ) : null}
                        <td className="pr-3 py-1.5">
                          {leg.holdingDays == null
                            ? "—"
                            : language === "en"
                              ? `${leg.holdingDays} days`
                              : `${leg.holdingDays} Tage`}
                        </td>
                        <td className="py-1.5 align-top">
                          {isUS ? (
                            <>
                              <div className="font-semibold text-slate-800">
                                {usTerm}
                              </div>
                              <div className="mt-0.5 text-[10px] text-slate-400">
                                {taxStatus(leg, language)}
                              </div>
                            </>
                          ) : (
                            taxStatus(leg, language)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {sourceLegs.length === 0 ? (
                    <tr className="border-t border-slate-200">
                      <td
                        colSpan={hasIndividualAllocation ? 9 : 7}
                        className="py-2 text-slate-500"
                      >
                        {language === "en"
                          ? "No source NFT details available."
                          : "Keine Details zu den eingesetzten NFTs verfügbar."}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {allocationExplanation ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">
              <span className="font-medium text-slate-700">
                {language === "en"
                  ? "Allocation method: "
                  : "Erlösaufteilung: "}
              </span>
              {allocationExplanation.replace(
                language === "en"
                  ? "Proceeds allocation: "
                  : "Erlösaufteilung: ",
                ""
              )}
            </div>
          ) : null}

          {isNoRealization ? (
            <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <div className="text-xs text-slate-500">
                  {language === "en" ? "Tax treatment" : "Steuerliche Behandlung"}
                </div>
                <div className="font-medium">
                  {language === "en" ? "No realization" : "Keine Realisierung"}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">
                  {language === "en"
                    ? "Historical source basis"
                    : "Historische AK Quellen"}
                </div>
                <div className="font-medium">
                  {money(totalBasis, currencySymbol)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">
                  {language === "en"
                    ? "Result NFT cost basis"
                    : "AK Ergebnis-NFT"}
                </div>
                <div className="font-medium">
                  {money(resultCost, currencySymbol)}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500">
                  {language === "en"
                    ? "Realized profit / loss"
                    : "Realisierter Gewinn / Verlust"}
                </div>
                <div className="font-medium">
                  {money(0, currencySymbol)}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <div>
                <div className="text-xs text-slate-500">
                  {language === "en" ? "Disposal proceeds" : "Veräußerungserlös"}
                </div>
                <div className="font-medium">
                  {money(totalExchangeValue, currencySymbol)}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500">
                  {language === "en" ? "Valuation side" : "Bewertungsseite"}
                </div>
                <div className="font-medium">{valuationBasisLabel}</div>
                {valuationBasisValue !== null ? (
                  <div className="mt-0.5 text-[10px] text-slate-400">
                    FMV {money(valuationBasisValue, currencySymbol)}
                  </div>
                ) : null}
              </div>

              <div>
                <div className="text-xs text-slate-500">
                  {language === "en" ? "Valuation source" : "Bewertungsquelle"}
                </div>
                <div className="font-medium leading-tight">
                  {valuationSourceLabel}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500">
                  {language === "en"
                    ? "Historical cost basis"
                    : "Historische AK gesamt"}
                </div>
                <div className="font-medium">
                  {money(totalBasis, currencySymbol)}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500">
                  {language === "en" ? "Profit / loss" : "Gewinn / Verlust"}
                </div>
                <div className="font-medium">
                  {money(grossProfit, currencySymbol)}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500">
                  {isUS
                    ? "ST / LT"
                    : language === "en"
                      ? "Taxable / tax-free"
                      : "Steuerpfl. / frei"}
                </div>
                <div className="font-medium">
                  {isUS ? (
                    <>
                      {money(shortTermProfit, currencySymbol)}
                      {" / "}
                      {money(longTermProfit, currencySymbol)}
                    </>
                  ) : (
                    <>
                      {money(taxableProfit, currencySymbol)}
                      {" / "}
                      {money(nonTaxableProfit, currencySymbol)}
                    </>
                  )}
                </div>
                {isUS ? (
                  <div className="mt-0.5 text-[10px] text-slate-400">
                    Short-Term / Long-Term
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <div className="border-t border-slate-200 pt-3 text-slate-800">
            <div className="font-medium">
              {language === "en" ? "Result NFT" : "Ergebnis-NFT"}: {resultName} #
              {resultTokenId}
            </div>
            <div className="mt-1 grid gap-2 sm:grid-cols-3">
              <span>
                {language === "en" ? "New acquisition" : "Neue Anschaffung"}:{" "}
                {resultTimestamp
                  ? formatDateTimeUtcFromTimestamp(resultTimestamp)
                  : "—"}
              </span>
              <span>
                {language === "en" ? "New cost basis" : "Neue AK"}:{" "}
                {money(resultCost, currencySymbol)}
                {isUS && !isNoRealization ? (
                  <span className="ml-1 text-[10px] text-slate-400">
                    {language === "en"
                      ? "(from exchange FMV)"
                      : "(aus Exchange-FMV)"}
                  </span>
                ) : null}
              </span>
              <span>
                {isItalyNeutral
                  ? language === "en"
                    ? "Holding period: neutral carryover"
                    : "Haltefrist: neutraler Carryover"
                  : language === "en"
                    ? "New holding period: yes"
                    : "Neue Haltefrist: ja"}
              </span>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}
